import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import DefaultApiLimit from '../../config/defaultLimit';
import { CommonFilterProperties } from '../../interface/filter.interface';
import { RequestOCPPLogsInterface, OCPPLogsInterface } from '../../interface/ocpp-logs-interface';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import {
  disableOCPPLogsFiltersApplied,
  fetchOCPPLogs,
  fetchOCPPLogsFilters,
  updateOCPPLogsFilters,
  setSortData,
  refreshOCPPLogs,
} from '../../redux/slices/OcppLogSlice';
import { setPageTitle } from '../../redux/slices/PageTitleSlice';
import CustomTable from '../../shared-components/custom-table/CustomTable';
import OcppLogSidePanel from '../../shared-components/table-side-panel/OcppLogsSidePanel';
import { GetParametersFromCommonFilters } from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import { exportCSV, exportPDF } from '../../shared-functions/ExportFile';
import FormatUtcString from '../../shared-functions/FormatUtcString';
import ocppLogsApi from '../../utils/api/ocpp-logs';

interface OCPPLogsProperties {
  account?: string;
  chargingStation?: string;
  chargingPointSn?: string;
  autoRefreshEnabled?: boolean;
  disableAutoRefreshToggle?: boolean;
  hideAccountStationChargerFilters?: boolean;
}

/**
 * Station activity component (OCPP Logs)
 * @param {OCPPLogsProperties} properties - Component properties
 * @returns {any} The rendered component
 */
function OCPPLogs(properties: OCPPLogsProperties = {}) {
  const {
    account,
    chargingStation,
    chargingPointSn,
    autoRefreshEnabled = false,
    disableAutoRefreshToggle = false,
    hideAccountStationChargerFilters = false,
  } = properties;

  const { t, i18n } = useTranslation('sidePanel');
  const dispatch = useAppDispatch();

  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [searchParameters, setSearchParameters] = useSearchParams();
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const {
    ocppLogs,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.ocppCPOLogs);
  const { selectedChargingPoint } = useAppSelector((state) => state.device);

  const targetChargingPointSn = chargingPointSn || selectedChargingPoint?.chargingPointSn;
  const targetChargingStation =
    chargingStation ||
    (selectedChargingPoint as any)?.chargingStationId ||
    (selectedChargingPoint as any)?.chargingStation ||
    (selectedChargingPoint as any)?.chargingStationName;
  const targetAccount =
    account ||
    (selectedChargingPoint as any)?.accountCustomId ||
    (selectedChargingPoint as any)?.accountName;

  /**
   * Helper function to get merged query parameters including fixed charger/station/account filters
   * @param {CommonFilterProperties[]} activeFilters The active filters list
   * @returns {any} The constructed parameters object
   */
  const getQueryParameters = (activeFilters: CommonFilterProperties[]) => {
    const queryParameters = GetParametersFromCommonFilters(activeFilters);
    if (targetChargingPointSn) {
      queryParameters.chargingPointSn = targetChargingPointSn;
    }
    if (targetChargingStation) {
      queryParameters.chargingStation = targetChargingStation;
    }
    if (targetAccount) {
      queryParameters.account = targetAccount;
    }
    return queryParameters;
  };

  const hiddenFilterKeys = [
    'account',
    'accountName',
    'accountCustomId',
    'cpo',
    'partner',
    'chargingStation',
    'chargingPointSn',
  ];

  const displayedFilters = useMemo(() => {
    if (!hideAccountStationChargerFilters) {
      return filters;
    }
    return filters.filter((filter) => !hiddenFilterKeys.includes(filter.key));
  }, [filters, hideAccountStationChargerFilters]);

  useEffect(() => {
    dispatch(setPageTitle(t('ocppLogs')));
    dispatch(fetchOCPPLogsFilters());
  }, [dispatch, t]);

  const column = useMemo(
    () => [
      {
        label: t('date'),
        accessorKey: 'created_at' as keyof OCPPLogsInterface,
        sortAllow: true,

        /**
         * Converting timestamp to UTC date
         * @param {any} ocppLog - The log object
         * @returns {any} The rendered element
         */
        customData: (ocppLog: OCPPLogsInterface) => <p>{ConvertUTCtoDate(ocppLog.created_at)}</p>,
      },
      {
        label: t('time'),
        accessorKey: 'time' as keyof OCPPLogsInterface,
        sortAllow: true,
        /**
         * Converting timestamp to UTC time
         * @param {any} ocppLogTime - The log object
         * @returns {any} The rendered element
         */
        customData: (ocppLogTime: OCPPLogsInterface) => (
          <p>{FormatUtcString(ConvertUTCtoHHMM(ocppLogTime.created_at))}</p>
        ),
      },
      {
        label: t('type'),
        accessorKey: 'type' as keyof OCPPLogsInterface,
        sortAllow: false,

        /**
         * Converting timestamp to UTC time
         * @param {any} ocppLog - The log object
         * @returns {any} The rendered element
         */
        customData: (ocppLog: OCPPLogsInterface) => {
          const type = ocppLog.type?.toLowerCase();

          return (
            <p className={`${type === 'in' ? 'type-in' : 'type-out'}`}>
              {type ? t(type as 'in' | 'out') : '-'}
            </p>
          );
        },
      },
      {
        label: t('messageType'),
        accessorKey: 'action' as keyof OCPPLogsInterface,
      },
      {
        label: t('message'),
        accessorKey: 'message' as keyof OCPPLogsInterface,
        columnWidth: 500,
      },
    ],
    [t],
  );

  /**
   * Handle the fetch the data in api function
   * @param {any} data The fetch params
   */
  const fetchData = (data: RequestOCPPLogsInterface) => {
    dispatch(
      fetchOCPPLogs({
        ...data,
      }),
    );
  };
  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('date'), accessorKey: 'created_at' },
    { label: t('time'), accessorKey: 'time' },
    { label: t('type'), accessorKey: 'type' },
    { label: t('actionType'), accessorKey: 'action' },
    { label: t('message'), accessorKey: 'message' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function for pdf and csv
   * @param {string} type pdf or csv
   */
  const exportDataPdfOrCSV = (type: string) => {
    const queryParameters = getQueryParameters(filters);
    setExportLoader(true);
    ocppLogsApi
      .fetchOCPPLogsExport({
        ...queryParameters,
      })
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map((row: OCPPLogsInterface, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;
              case 'created_at':
                return ConvertUTCtoDate(row?.created_at) || 'N/A';
              case 'time':
                return ConvertUTCtoHHMM(row?.created_at) || 'N/A';
              default:
                return (row as any)[col?.accessorKey] || 'N/A';
            }
          }),
        );
        if (type === 'csv') {
          exportCSV(headers, rows, 'OCPP Logs.csv');
        } else {
          exportPDF(headers, rows, 'OCPP Logs.pdf');
        }
        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };
  /**
   * Table pagination function
   * @param {any} value The page props
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = getQueryParameters(filters);
    fetchData({
      ...value,
      ...queryParameters,
      sortBy,
      sortField,
    });
  };
  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = getQueryParameters(filters);
    fetchData({
      ...queryParameters,
      page,
      limit,
      sortBy,
      sortField,
    });
  };

  /**
   * Function for Auto Refresh the data
   */
  const handleAutoRefresh = useCallback(() => {
    const queryParameters = getQueryParameters(filters);

    dispatch(
      refreshOCPPLogs({
        ...queryParameters,
        page,
        limit,
        sortBy,
        sortField,
      }),
    );
  }, [dispatch, filters, limit, page, sortBy, sortField, targetAccount, targetChargingPointSn, targetChargingStation]);

  /**
   * Function for sorting table data
   * @param {object} sortData The sort configuration
   * @param {any} sortData.sortBy - The sortBy prop
   * @param {any} sortData.sortField - The sortField prop
   */
  const handleSortFunction = (sortData: { sortBy: 'asc' | 'desc'; sortField: string }) => {
    const queryParameters = getQueryParameters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function to add search params, side panel will open automatically
   * @param {string} logId The log id
   */
  const handleOpenSidePanelOnRowClick = (logId: string) => {
    // Set or update the logId param
    searchParameters.set('logId', logId);

    // Apply the updated search params to the URL
    setSearchParameters(searchParameters);
  };

  useEffect(() => {
    // Trigger data fetch when filter list is loaded for the first time
    // or when user updates filters (isFilterUpdated flag).
    const hasStationFilter = filters.some((f) => f.key === 'chargingStation');
    const shouldFetchInitial =
      !filterListLoading &&
      (hasStationFilter || hideAccountStationChargerFilters || targetChargingPointSn) &&
      !isFilterUpdated;
    const shouldFetchUpdated = isFilterUpdated;
    if (shouldFetchInitial || shouldFetchUpdated) {
      const queryParameters = getQueryParameters(filters);
      fetchData({
        ...queryParameters,
        page: DefaultApiLimit.page,
        limit,
        sortBy,
        sortField,
      });
      if (shouldFetchUpdated) {
        dispatch(disableOCPPLogsFiltersApplied());
      }
    }
  }, [
    filterListLoading,
    filters,
    isFilterUpdated,
    sortBy,
    sortField,
    limit,
    hideAccountStationChargerFilters,
    targetChargingPointSn,
    targetChargingStation,
    targetAccount,
  ]);

  useEffect(() => {
    dispatch(
      setPageTitle({
        title: t('oCPPLogs'),
        count: totalResults,
      }),
    );
  }, [totalResults]);

  return (
    <div className="station-activity-wrap" dir={i18n.dir()}>
      <div className="station-activity-table">
        <CustomTable
          version="v2"
          header={column}
          data={ocppLogs}
          serialNo
          sizeChangeButtonRequired
          limit={limit}
          page={page}
          totalPages={totalPages}
          totalResults={totalResults}
          loading={loading}
          filterListLoading={filterListLoading}
          filters={displayedFilters}
          filtersToDispatch={updateOCPPLogsFilters}
          filterListToDispatch={fetchOCPPLogsFilters}
          exportDataPdfOrCSV={exportDataPdfOrCSV}
          exportLoader={exportLoader}
          refershCurrentPages={handleRefresh}
          requiredRefreshToggle
          refreshInterval={15}
          refreshFunction={handleAutoRefresh}
          autoRefreshEnabled={autoRefreshEnabled}
          disableAutoRefreshToggle={disableAutoRefreshToggle}
          pageLimitChange={pageNateLimitChange}
          sortData={{
            sortBy,
            sortField,
          }}
          height={340}
          handleSortChange={handleSortFunction}
          rowClickEvent={handleOpenSidePanelOnRowClick}
          rowClickParamsKey="logId"
          rowClickSidePanel={<OcppLogSidePanel fetchIdFrom="queryParams" />}
          onHiddenColumnsChange={setHiddenColumns}
        />
      </div>
    </div>
  );
}

OCPPLogs.defaultProps = {
  account: '',
  chargingStation: '',
  chargingPointSn: '',
  autoRefreshEnabled: false,
  disableAutoRefreshToggle: false,
  hideAccountStationChargerFilters: false,
};

export default OCPPLogs;
